import React, { createContext, useContext, useState } from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';

import { MessageType } from '@savgroup-front-common/types';

import { FieldMessages } from '../common/helpers/getFinalFieldState.types';

interface WarningContextType {
  warnings: FieldMessages;
  setWarning: (name: string, message: MessageType | string) => void;
  clearWarning: (name: string) => void;
  clearAllWarnings: () => void;
}

const WarningContext = createContext<WarningContextType | undefined>(undefined);

export const useWarningFormContext = () => {
  const context = useContext(WarningContext);

  if (!context) {
    throw new Error(
      'useWarningFormContext must be used within a WarningFormProvider',
    );
  }

  return context;
};

export function WarningFormProvider<
  T extends FieldValues,
  U extends FieldValues,
  V extends FieldValues,
>({
  children,
  formContext,
}: {
  children: React.ReactNode;
  formContext: UseFormReturn<T, U, V>;
}) {
  const [warnings, setWarnings] = useState<FieldMessages>({});

  const setWarning = (name: string, message: MessageType | string) => {
    setWarnings({
      ...warnings,
      [name]: message,
    } as FieldMessages);
  };

  const clearWarning = (name: string) => {
    setWarnings((prev) => {
      return {
        ...prev,
        [name]: undefined,
      } as FieldMessages;
    });
  };

  const clearAllWarnings = () => {
    setWarnings({});
  };

  const extendedFormContext = {
    ...formContext,
    warnings,
  };

  return (
    <WarningContext.Provider
      value={{ warnings, setWarning, clearWarning, clearAllWarnings }}
    >
      <FormProvider {...extendedFormContext}>{children}</FormProvider>
    </WarningContext.Provider>
  );
}
